(() => {
  const doc = document;
  const shopRoot = (() => {
    const raw = window.Shopify?.routes?.root || '/';
    return raw.endsWith('/') ? raw : `${raw}/`;
  })();
  const route = path => `${shopRoot}${String(path).replace(/^\//, '')}`;
  const productCache = new Map();
  const CART_ICON = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 4.75h1.45c.42 0 .79.28.91.68L6.08 8H20.5c.31 0 .6.15.78.41.18.25.23.58.14.87l-1.63 5.56a1 1 0 0 1-.96.72H9.02a1 1 0 0 1-.96-.74L6.12 7.5H5.5" />
      <circle cx="10" cy="19" r="1.6" />
      <circle cx="18" cy="19" r="1.6" />
    </svg>`;

  const formatMoney = cents => {
    const value = (Number(cents) || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'BDT';
    if (currency === 'BDT') {
      return `৳${value.toLocaleString('en-BD', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    }
    try {
      return new Intl.NumberFormat(doc.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2
      }).format(value);
    } catch (_) {
      return `${value.toFixed(2)} ${currency}`;
    }
  };

  const paintCartBadge = count => {
    const quantity = Math.max(0, Number(count) || 0);
    doc.querySelectorAll('.rr-cart').forEach(link => {
      const badge = link.querySelector('b');
      if (badge) {
        badge.textContent = quantity > 99 ? '99+' : String(quantity);
        badge.hidden = quantity === 0;
      }
      link.setAttribute('aria-label', `Cart, ${quantity} ${quantity === 1 ? 'item' : 'items'}`);
      link.classList.remove('cs-cart-pulse');
      void link.offsetWidth;
      link.classList.add('cs-cart-pulse');
      window.setTimeout(() => link.classList.remove('cs-cart-pulse'), 650);
    });
  };

  const refreshCart = async () => {
    const response = await fetch(route('cart.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Could not refresh the cart.');
    const cart = await response.json();
    paintCartBadge(cart.item_count);
    doc.dispatchEvent(new CustomEvent('clicksubs:cart-updated', { detail: { cart } }));
    return cart;
  };

  const refreshCartSoftly = async () => {
    try {
      return await refreshCart();
    } catch (_) {
      window.setTimeout(() => refreshCart().catch(() => {}), 900);
      return null;
    }
  };

  const ensureToastHost = () => {
    let host = doc.querySelector('[data-cs-cart-toast-host]');
    if (host) return host;
    host = doc.createElement('div');
    host.className = 'cs-cart-toast-host';
    host.dataset.csCartToastHost = '';
    host.setAttribute('aria-live', 'polite');
    host.setAttribute('aria-atomic', 'true');
    doc.body.appendChild(host);
    return host;
  };

  const showToast = (heading, detail = '', error = false) => {
    const host = ensureToastHost();
    const toast = doc.createElement('div');
    toast.className = `cs-cart-toast${error ? ' is-error' : ''}`;
    toast.setAttribute('role', error ? 'alert' : 'status');

    const icon = doc.createElement('span');
    icon.className = 'cs-cart-toast__icon';
    icon.innerHTML = CART_ICON;

    const copy = doc.createElement('span');
    copy.className = 'cs-cart-toast__copy';
    const strong = doc.createElement('strong');
    strong.textContent = heading;
    copy.appendChild(strong);
    if (detail) {
      const small = doc.createElement('small');
      small.textContent = detail;
      copy.appendChild(small);
    }

    toast.append(icon, copy);
    host.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 260);
    }, error ? 3600 : 2600);
  };

  const requestAdd = async body => {
    const response = await fetch(route('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: body instanceof FormData
        ? { Accept: 'application/json' }
        : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body instanceof FormData ? body : JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || 'This item could not be added to the cart.');
    }

    // The add itself succeeded. A secondary cart-count refresh must never turn
    // a successful add into a false error message for the shopper.
    await refreshCartSoftly();
    return data;
  };

  const addVariant = (variantId, quantity = 1) => requestAdd({
    items: [{ id: variantId, quantity: Math.max(1, Number(quantity) || 1) }]
  });

  const productHandleFromUrl = urlValue => {
    try {
      const url = new URL(urlValue, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  };

  const fetchProductForButton = button => {
    const handle = productHandleFromUrl(button.dataset.productUrl || '');
    if (!handle) return Promise.reject(new Error('Product information is unavailable.'));
    if (productCache.has(handle)) return productCache.get(handle);

    const promise = fetch(route(`products/${encodeURIComponent(handle)}.js`), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(response => {
      if (!response.ok) throw new Error('Product information is unavailable.');
      return response.json();
    }).catch(error => {
      productCache.delete(handle);
      throw error;
    });

    productCache.set(handle, promise);
    return promise;
  };

  const setQuickButtonBusy = (button, busy) => {
    if (!button) return;
    button.classList.toggle('is-busy', busy);
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.disabled = busy;
  };

  const markQuickButtonAdded = button => {
    if (!button) return;
    button.classList.remove('is-busy');
    button.classList.add('is-added');
    button.setAttribute('aria-busy', 'false');
    button.disabled = false;
    window.setTimeout(() => button.classList.remove('is-added'), 950);
  };

  const ensureQuickDialog = () => {
    let dialog = doc.querySelector('[data-cs-quick-dialog]');
    if (dialog) return dialog;

    dialog = doc.createElement('dialog');
    dialog.className = 'cs-quick-cart-dialog';
    dialog.dataset.csQuickDialog = '';
    dialog.setAttribute('aria-labelledby', 'CsQuickCartTitle');
    dialog.innerHTML = `
      <button class="cs-quick-cart-dialog__close" type="button" data-cs-quick-close aria-label="Close quick add">×</button>
      <div class="cs-quick-cart-dialog__icon">${CART_ICON}</div>
      <div class="cs-quick-cart-dialog__eyebrow">QUICK ADD</div>
      <h2 id="CsQuickCartTitle" data-cs-quick-title></h2>
      <label class="cs-quick-cart-dialog__field">
        <span>Choose a plan</span>
        <select data-cs-quick-select></select>
      </label>
      <div class="cs-quick-cart-dialog__price" data-cs-quick-price></div>
      <button class="cs-quick-cart-dialog__add" type="button" data-cs-quick-confirm>ADD TO CART</button>`;
    doc.body.appendChild(dialog);

    const close = () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    };

    dialog.querySelector('[data-cs-quick-close]').addEventListener('click', close);
    dialog.addEventListener('cancel', event => {
      event.preventDefault();
      close();
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) close();
    });

    const select = dialog.querySelector('[data-cs-quick-select]');
    const price = dialog.querySelector('[data-cs-quick-price]');
    select.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      price.textContent = option?.dataset.price ? `Price: ${option.dataset.price}` : '';
    });

    dialog.querySelector('[data-cs-quick-confirm]').addEventListener('click', async () => {
      const confirm = dialog.querySelector('[data-cs-quick-confirm]');
      const option = select.selectedOptions[0];
      const trigger = dialog._csTrigger;
      if (!option || option.disabled || !option.value || confirm.disabled) return;

      const original = confirm.textContent;
      confirm.disabled = true;
      confirm.textContent = 'ADDING…';
      try {
        await addVariant(option.value, 1);
        close();
        markQuickButtonAdded(trigger);
        showToast('Added to cart', dialog._csProductTitle || 'Item added');
      } catch (error) {
        setQuickButtonBusy(trigger, false);
        showToast('Could not add to cart', error.message || 'Please try again.', true);
      } finally {
        confirm.disabled = false;
        confirm.textContent = original;
      }
    });

    return dialog;
  };

  const openVariantDialog = (product, trigger) => {
    const dialog = ensureQuickDialog();
    const title = dialog.querySelector('[data-cs-quick-title]');
    const select = dialog.querySelector('[data-cs-quick-select]');
    const price = dialog.querySelector('[data-cs-quick-price]');
    const confirm = dialog.querySelector('[data-cs-quick-confirm]');

    title.textContent = product.title || 'Choose a plan';
    select.replaceChildren();
    let firstAvailable = null;

    (product.variants || []).forEach(variant => {
      const option = doc.createElement('option');
      option.value = String(variant.id);
      option.disabled = !variant.available;
      option.dataset.price = formatMoney(variant.price);
      option.textContent = `${variant.title}${variant.available ? '' : ' — Sold out'}`;
      if (!firstAvailable && variant.available) firstAvailable = option;
      select.appendChild(option);
    });

    if (firstAvailable) {
      select.value = firstAvailable.value;
      price.textContent = `Price: ${firstAvailable.dataset.price}`;
      confirm.disabled = false;
      confirm.textContent = 'ADD TO CART';
    } else {
      price.textContent = 'Currently unavailable';
      confirm.disabled = true;
      confirm.textContent = 'SOLD OUT';
    }

    dialog._csTrigger = trigger;
    dialog._csProductTitle = product.title || 'Item added';
    setQuickButtonBusy(trigger, false);

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  };

  const enhanceProductCards = scope => {
    const root = scope?.querySelectorAll ? scope : doc;
    const cards = [];
    if (root.matches?.('.cs2-product-card, .cs-product-card')) cards.push(root);
    root.querySelectorAll?.('.cs2-product-card, .cs-product-card').forEach(card => cards.push(card));

    cards.forEach(card => {
      if (card.dataset.csQuickCartEnhanced === 'true') return;
      const media = card.querySelector('a.cs2-product-card__media[href*="/products/"], a.cs-product-card__media[href*="/products/"]')
        || card.querySelector('a[href*="/products/"]');
      if (!media) return;

      card.dataset.csQuickCartEnhanced = 'true';
      const wrapper = doc.createElement('div');
      wrapper.className = 'cs-quick-card-media';
      media.parentNode.insertBefore(wrapper, media);
      wrapper.appendChild(media);

      const button = doc.createElement('button');
      button.type = 'button';
      button.className = 'cs-quick-cart-button';
      button.dataset.csQuickCart = '';
      button.dataset.productUrl = media.href;
      const cardTitle = card.querySelector('h3')?.textContent?.trim() || 'product';
      button.setAttribute('aria-label', `Quick add ${cardTitle} to cart`);
      button.title = 'Quick add to cart';
      button.innerHTML = CART_ICON;
      wrapper.appendChild(button);
    });
  };

  doc.addEventListener('click', async event => {
    const button = event.target.closest?.('[data-cs-quick-cart]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    if (button.disabled || button.classList.contains('is-busy')) return;

    setQuickButtonBusy(button, true);
    try {
      const product = await fetchProductForButton(button);
      const variants = product.variants || [];
      if (!variants.length) throw new Error('No purchasable plan was found.');

      if (variants.length === 1) {
        const variant = variants[0];
        if (!variant.available) throw new Error('This item is currently sold out.');
        await addVariant(variant.id, 1);
        markQuickButtonAdded(button);
        showToast('Added to cart', product.title || 'Item added');
      } else {
        openVariantDialog(product, button);
      }
    } catch (error) {
      setQuickButtonBusy(button, false);
      showToast('Could not add to cart', error.message || 'Please try again.', true);
    }
  });

  const isNormalAddSubmit = (form, submitter) => {
    if (!form?.action?.includes('/cart/add')) return false;
    if (form.dataset.csAjaxCart === 'off') return false;
    if (submitter?.name === 'checkout') return false;
    if (submitter?.closest?.('.shopify-payment-button, [data-shopify-buttoncontainer]')) return false;
    if (submitter && !submitter.matches('[name="add"], [data-add-button], [data-cs-ajax-add]')) return false;
    return Boolean(form.querySelector('input[name="id"], select[name="id"]'));
  };

  doc.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submit = event.submitter || form.querySelector('[type="submit"][name="add"], [data-add-button]');
    if (!isNormalAddSubmit(form, event.submitter)) return;

    event.preventDefault();
    if (form.dataset.csAjaxBusy === 'true' || submit?.disabled) return;
    form.dataset.csAjaxBusy = 'true';

    const original = submit ? submit.innerHTML : '';
    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      submit.textContent = 'ADDING…';
    }

    const title = form.closest('[data-product-root]')?.querySelector('.cs2-product-title')?.textContent?.trim()
      || form.closest('.cs2-product-card, .cs-product-card')?.querySelector('h3')?.textContent?.trim()
      || 'Item added';

    try {
      await requestAdd(new FormData(form));
      showToast('Added to cart', title);
    } catch (error) {
      showToast('Could not add to cart', error.message || 'Please try again.', true);
    } finally {
      delete form.dataset.csAjaxBusy;
      if (submit) {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        submit.innerHTML = original;
      }
    }
  }, true);

  const init = scope => enhanceProductCards(scope || doc);
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => init(doc), { once: true });
  else init(doc);

  doc.addEventListener('shopify:section:load', event => init(event.target));

  const startObserver = () => {
    if (!doc.body) return;
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) enhanceProductCards(node);
      }));
    });
    observer.observe(doc.body, { childList: true, subtree: true });
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', startObserver, { once: true });
  else startObserver();
})();
